/**
 * OCP-985 - the agentLanes entitlement row on the tenant and license detail.
 *
 * Shows the paid hosted agent lanes (included + booked) and where `included`
 * comes from (tier default, partner site package, operator override, or
 * Self-Host: none counted). "Edit lanes" opens a dialog that sets booked
 * lanes, the included override (empty = back to the tier default) and, on a
 * hosted workspace, the partner site package sites - with a required reason
 * (10..500 characters) that lands on the audit row. Same route the
 * `ocp_set_agent_lanes` MCP tool calls (OCP-599: every operator action is
 * agent-operable with the same audit trail).
 */
import { useState } from 'react';
import {
  AGENT_LANE_PRICE_EUR_PER_MONTH,
  AGENT_LANES_MAX,
  AGENT_LANES_REASON_MAX,
  AGENT_LANES_REASON_MIN,
  type AgentLanesView,
} from '@orboto/ocp-shared';

import { api, ApiError, readErrorKey } from '../lib/api';

export type AgentLanesTarget = { kind: 'tenant'; id: string } | { kind: 'license'; id: string };

const SOURCE_LABEL: Record<AgentLanesView['included_source'], string> = {
  tier_default: 'tier default',
  partner_package: 'partner site package',
  override: 'operator override',
  self_host: 'Self-Host: lanes on own infrastructure are not counted',
  no_tier: 'no plan tier known',
};

export function agentLanesSummary(v: AgentLanesView): string {
  return `${v.paid} paid (${v.included} included + ${v.booked} booked)`;
}

export function agentLanesHint(v: AgentLanesView): string {
  const src =
    v.included_source === 'tier_default'
      ? `included: tier default${v.tier ? ` (${v.tier})` : ''}`
      : v.included_source === 'partner_package'
        ? `included: partner site package, ${v.partner_sites} site${v.partner_sites === 1 ? '' : 's'} x 3`
        : v.included_source === 'override'
          ? `included: operator override (default would be ${v.tier_default})`
          : `included: ${SOURCE_LABEL[v.included_source]}`;
  return `${src}; ${AGENT_LANE_PRICE_EUR_PER_MONTH} EUR per booked lane and month`;
}

export function AgentLanesField({
  target,
  lanes,
  editable,
  onChanged,
}: {
  target: AgentLanesTarget;
  lanes: AgentLanesView;
  /** false = read-only (deleted workspace, revoked license, registered self-host instance). */
  editable: boolean;
  onChanged: (next: AgentLanesView) => void;
}) {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-3 shadow-sm" data-testid="agent-lanes-field">
      <div className="flex items-start justify-between gap-2">
        <div className="text-xs font-medium uppercase tracking-wide text-gray-500">Agent lanes</div>
        {editable && (
          <button type="button" className="text-xs text-brand-600 underline" onClick={() => { setMessage(null); setOpen(true); }}>
            Edit lanes
          </button>
        )}
      </div>
      <div className="mt-1 text-sm font-medium text-gray-900 dark:text-gray-100">{agentLanesSummary(lanes)}</div>
      <div className="mt-0.5 text-xs text-gray-500">{agentLanesHint(lanes)}</div>
      {message && <div className="mt-1 text-xs text-gray-700 dark:text-gray-300" role="status">{message}</div>}
      {open && (
        <AgentLanesDialog
          target={target}
          lanes={lanes}
          onCancel={() => setOpen(false)}
          onSaved={(next, delivery) => {
            setOpen(false);
            setMessage(`Saved: ${agentLanesSummary(next)}. ${delivery}`);
            onChanged(next);
          }}
        />
      )}
    </div>
  );
}

function parseCount(raw: string): number | null | 'invalid' {
  if (raw.trim() === '') return null;
  const n = Number(raw);
  return Number.isInteger(n) && n >= 0 && n <= AGENT_LANES_MAX ? n : 'invalid';
}

export function AgentLanesDialog({
  target,
  lanes,
  onCancel,
  onSaved,
}: {
  target: AgentLanesTarget;
  lanes: AgentLanesView;
  onCancel: () => void;
  onSaved: (next: AgentLanesView, delivery: string) => void;
}) {
  const [booked, setBooked] = useState(String(lanes.booked));
  const [override, setOverride] = useState(lanes.included_override === null ? '' : String(lanes.included_override));
  const [sites, setSites] = useState(String(lanes.partner_sites));
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const bookedN = parseCount(booked);
  const overrideN = parseCount(override);
  const sitesN = parseCount(sites);
  const isTenant = target.kind === 'tenant';
  const body: Record<string, unknown> = {};
  if (typeof bookedN === 'number' && bookedN !== lanes.booked) body.booked = bookedN;
  if (overrideN !== 'invalid' && overrideN !== lanes.included_override) body.included_override = overrideN;
  if (isTenant && typeof sitesN === 'number' && sitesN !== lanes.partner_sites) body.partner_sites = sitesN;
  const trimmed = reason.trim();
  const reasonOk = trimmed.length >= AGENT_LANES_REASON_MIN && trimmed.length <= AGENT_LANES_REASON_MAX;
  const valuesOk = bookedN !== 'invalid' && bookedN !== null && overrideN !== 'invalid' && (!isTenant || (sitesN !== 'invalid' && sitesN !== null));
  const changed = Object.keys(body).length > 0;

  async function submit() {
    if (saving || !reasonOk || !valuesOk || !changed) return;
    setSaving(true);
    setError(null);
    try {
      const path = isTenant ? `/v1/admin/tenants/${target.id}/agent-lanes` : `/v1/admin/licenses/${target.id}/agent-lanes`;
      const r = await api<{ agent_lanes: AgentLanesView }>(path, { method: 'PATCH', body: { ...body, reason: trimmed } });
      onSaved(r.agent_lanes, r.agent_lanes.delivery);
    } catch (err) {
      const msg = err instanceof ApiError ? (err.body as { message?: string } | null)?.message : null;
      setError(err instanceof ApiError ? msg ?? readErrorKey(err.body) ?? `Save failed (HTTP ${err.status}).` : 'Save failed.');
    } finally {
      setSaving(false);
    }
  }

  const inputCls = 'mt-1 w-24 rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-2 py-1.5 text-sm';
  return (
    <div role="dialog" aria-modal="true" aria-label="Edit agent lanes" className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/40 px-4" onClick={onCancel}>
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-md rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-6 shadow-xl">
        <h2 className="mb-1 text-base font-semibold">Edit agent lanes</h2>
        <p className="mb-3 text-sm text-gray-600 dark:text-gray-400">
          Paid lanes = included + booked. Core warns over the paid number and never locks a feature.{' '}
          {isTenant
            ? 'The workspace gets the new value with its next redeploy or maintenance roll.'
            : 'The instance gets the new value at its next license verify.'}
        </p>
        <div className="flex flex-wrap gap-4 text-sm">
          <label className="block">
            <span className="text-gray-700 dark:text-gray-300">Booked</span>
            <input type="number" min={0} max={AGENT_LANES_MAX} aria-label="Booked lanes" value={booked} onChange={(e) => setBooked(e.target.value)} className={inputCls} />
          </label>
          <label className="block">
            <span className="text-gray-700 dark:text-gray-300">Included override</span>
            <input
              type="number"
              min={0}
              max={AGENT_LANES_MAX}
              aria-label="Included override"
              placeholder={`default ${lanes.tier_default}`}
              value={override}
              onChange={(e) => setOverride(e.target.value)}
              className={inputCls}
            />
          </label>
          {isTenant && (
            <label className="block">
              <span className="text-gray-700 dark:text-gray-300">Partner sites</span>
              <input type="number" min={0} max={AGENT_LANES_MAX} aria-label="Partner sites" value={sites} onChange={(e) => setSites(e.target.value)} className={inputCls} />
            </label>
          )}
        </div>
        <p className="mt-1 text-xs text-gray-500">Leave the override empty to use the tier default ({lanes.tier_default}).</p>
        <label className="mt-3 block text-sm">
          <span className="text-gray-700 dark:text-gray-300">
            Reason (recorded in the audit log, {AGENT_LANES_REASON_MIN}..{AGENT_LANES_REASON_MAX} characters)
          </span>
          <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} aria-label="Reason" className="mt-1 w-full rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-2 py-1.5 text-sm" />
        </label>
        {!valuesOk && <div className="mt-2 text-xs text-red-700 dark:text-red-300">Lane numbers are whole numbers from 0 to {AGENT_LANES_MAX}.</div>}
        {error && <div role="alert" className="mt-2 text-xs text-red-700 dark:text-red-300">{error}</div>}
        <div className="mt-4 flex justify-end gap-2">
          <button type="button" onClick={onCancel} className="rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-1.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800/60">
            Cancel
          </button>
          <button
            type="button"
            disabled={saving || !reasonOk || !valuesOk || !changed}
            onClick={() => void submit()}
            className="rounded bg-brand-600 px-3 py-1.5 text-sm text-white disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Save lanes'}
          </button>
        </div>
      </div>
    </div>
  );
}
